import { useEffect, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useMutation, useQuery } from "@apollo/client";
import type { DateValueType } from "react-tailwindcss-datepicker";
import { DateTime } from "luxon";
import Button from "@/components/Button";
import DateSelect from "@/components/DateSelect";
import Dialog from "@/components/Dialog";
import Select from "@/components/Select";
import Banner from "@/components/Banner";
import { showErrorSnackbar, showSnackbar } from "@/lib/snackbarUtils";
import { buildPgmLevelSelectOptions } from "@/features/person/utils/education-program-level-options";
import { GET_EDUCATION_PROGRAM_LEVELS_QUERY, GET_REAFFILIATE_UNIVERSITY_CATALOG_QUERY } from "./queries";
import {
  PREVIEW_REAFFILIATE_UNIVERSITY_STUDENT_MUTATION,
  REAFFILIATE_UNIVERSITY_STUDENT_MUTATION,
  reaffiliateUniversityStudentRefetchQueries
} from "./mutations";

type FormValues = {
  universityLegacyId: string;
  levelCode: string;
  startDate: string;
};

interface ReaffiliateUniversityStudentModalProps {
  onClose(): void;
  open: boolean;
  pgaId: string;
  defaultUniversityLegacyId?: string | null;
}

type PreviewState = {
  canProceed: boolean;
  errors: string[];
  warnings: string[];
  plannedActions: string[];
  cohortName?: string | null;
  inputKey: string;
};

function buildDefaultValues(defaultUniversityLegacyId?: string | null): FormValues {
  return {
    universityLegacyId: defaultUniversityLegacyId || "",
    levelCode: "",
    startDate: DateTime.local().toISODate() || ""
  };
}

function formInputKey(values: FormValues): string {
  return `${values.universityLegacyId}|${values.levelCode}|${values.startDate}`;
}

export default function ReaffiliateUniversityStudentModal({
  onClose,
  open,
  pgaId,
  defaultUniversityLegacyId
}: ReaffiliateUniversityStudentModalProps) {
  const [preview, setPreview] = useState<PreviewState | null>(null);

  const { data: universitiesData, loading: loadingUniversities } = useQuery(GET_REAFFILIATE_UNIVERSITY_CATALOG_QUERY, {
    skip: !open,
    fetchPolicy: "cache-first"
  });

  const {
    data: levelsData,
    loading: loadingLevels,
    error: levelsError
  } = useQuery(GET_EDUCATION_PROGRAM_LEVELS_QUERY, {
    skip: !open,
    fetchPolicy: "cache-first"
  });

  const universityOptions = useMemo(
    () =>
      (universitiesData?.universities || [])
        .slice()
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((university) => ({
          value: university.legacyId,
          label: university.name
        })),
    [universitiesData?.universities]
  );

  const levelOptions = useMemo(
    () =>
      buildPgmLevelSelectOptions(
        (levelsData?.educationProgramLevels || []).filter((level) => level.levelCode.startsWith("V"))
      ),
    [levelsData?.educationProgramLevels]
  );

  const [previewReaffiliate, { loading: previewing }] = useMutation(PREVIEW_REAFFILIATE_UNIVERSITY_STUDENT_MUTATION);
  const [reaffiliateStudent, { loading: saving }] = useMutation(REAFFILIATE_UNIVERSITY_STUDENT_MUTATION, {
    refetchQueries: reaffiliateUniversityStudentRefetchQueries(pgaId),
    awaitRefetchQueries: true
  });

  const { control, handleSubmit, reset, watch, getValues } = useForm<FormValues>({
    defaultValues: buildDefaultValues(defaultUniversityLegacyId)
  });

  const universityLegacyId = watch("universityLegacyId");
  const levelCode = watch("levelCode");
  const startDate = watch("startDate");
  const currentFormValues = { universityLegacyId, levelCode, startDate };
  const currentInputKey = formInputKey(currentFormValues);
  const previewMatchesForm = preview?.inputKey === currentInputKey;
  const canConfirm = previewMatchesForm && preview?.canProceed === true;
  const formLocked = previewing || saving;

  useEffect(() => {
    if (!open) {
      return;
    }

    reset(buildDefaultValues(defaultUniversityLegacyId));
    setPreview(null);
  }, [defaultUniversityLegacyId, open, reset]);

  const handleClose = () => {
    reset(buildDefaultValues(defaultUniversityLegacyId));
    setPreview(null);
    onClose();
  };

  const buildInput = (values: FormValues) => ({
    pgaId,
    universityLegacyId: values.universityLegacyId,
    levelCode: values.levelCode,
    startDate: values.startDate
  });

  const runPreview = async (values: FormValues) => {
    const inputKey = formInputKey(values);

    try {
      const { data } = await previewReaffiliate({
        variables: { input: buildInput(values) }
      });

      if (formInputKey(getValues()) !== inputKey) {
        return;
      }

      const payload = data?.previewReaffiliateUniversityStudent;
      if (!payload) {
        showErrorSnackbar("Unable to preview re-affiliation.");
        return;
      }

      setPreview({
        canProceed: payload.canProceed,
        errors: payload.errors,
        warnings: payload.warnings,
        plannedActions: payload.plannedActions,
        cohortName: payload.cohortName,
        inputKey
      });

      if (!payload.canProceed) {
        showErrorSnackbar(payload.errors[0] || payload.message || "Re-affiliation cannot proceed.");
      }
    } catch {
      showErrorSnackbar("Unable to preview re-affiliation.");
    }
  };

  const onSubmit = async (values: FormValues) => {
    if (!canConfirm) {
      await runPreview(values);
      return;
    }

    try {
      const { data } = await reaffiliateStudent({
        variables: { input: buildInput(values) }
      });

      const payload = data?.reaffiliateUniversityStudent;
      if (!payload?.success) {
        showErrorSnackbar(payload?.message || "Unable to re-affiliate student.");
        return;
      }

      showSnackbar(payload.message || "Student re-affiliated");
      handleClose();
    } catch {
      showErrorSnackbar("Unable to re-affiliate student.");
    }
  };

  const primaryLabel = canConfirm ? "Re-Affiliate Student" : "Preview Changes";
  const isPrimaryDisabled =
    saving ||
    previewing ||
    loadingUniversities ||
    loadingLevels ||
    !!levelsError ||
    !universityLegacyId ||
    !levelCode ||
    !startDate;

  return (
    <Dialog
      open={open}
      onClosed={handleClose}
      headline="Re-Affiliate University Student"
      className="w-[560px] h-fit max-h-[90vh]"
      hasFormElements
      displayOverflow
      actions={
        <div className="flex gap-3">
          <Button variant="text" onClick={handleClose} disabled={saving || previewing}>
            Cancel
          </Button>
          <Button
            variant="filled"
            onClick={handleSubmit(onSubmit)}
            disabled={isPrimaryDisabled}
            testId="reaffiliate-university-student-submit"
          >
            {primaryLabel}
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-6">
        <Banner
          variant="info"
          description="Use this flow when a terminated PGM student is returning to the same university. It creates a new student band, reactivates membership, and enrolls the student in a cohort."
        />

        <Controller
          name="universityLegacyId"
          control={control}
          rules={{ required: "University is required" }}
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <Select
              label="University"
              value={value}
              options={universityOptions}
              onSelect={(nextValue) => {
                onChange(nextValue);
                setPreview(null);
              }}
              required
              disabled={loadingUniversities || formLocked}
              error={!!error}
              errorText={error?.message}
              data-testid="reaffiliate-university-select"
            />
          )}
        />

        <Controller
          name="levelCode"
          control={control}
          rules={{ required: "Level is required" }}
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <Select
              label="PGM Level"
              value={value}
              options={levelOptions}
              onSelect={(nextValue) => {
                onChange(nextValue);
                setPreview(null);
              }}
              required
              disabled={loadingLevels || !!levelsError || formLocked}
              error={!!error || !!levelsError}
              errorText={levelsError ? "Unable to load levels" : error?.message}
              data-testid="reaffiliate-level-code"
            />
          )}
        />

        <Controller
          name="startDate"
          control={control}
          rules={{ required: "Start date is required" }}
          render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
            <DateSelect
              label="Start Date"
              disabled={formLocked}
              value={
                value
                  ? {
                      startDate: value,
                      endDate: value
                    }
                  : null
              }
              onChange={(dateValue: DateValueType | string | null) => {
                if (typeof dateValue === "string") return;
                const startDate = dateValue?.startDate;
                onChange(typeof startDate === "string" ? startDate : "");
                setPreview(null);
              }}
              onBlur={onBlur}
              useSingleDate
              useMultiCalendar={false}
              useModal
              error={!!error}
              errorMessage={error?.message}
            />
          )}
        />

        {preview && previewMatchesForm ? (
          <div className="flex flex-col gap-4" data-testid="reaffiliate-preview-panel">
            {preview.errors.length > 0 ? (
              <Banner
                variant="error"
                description={preview.errors.join(" ")}
                inline
                closable={false}
                hideSecondaryDismiss
              />
            ) : null}
            {preview.warnings.map((warning) => (
              <Banner key={warning} variant="warn" description={warning} inline closable={false} hideSecondaryDismiss />
            ))}
            {preview.cohortName ? (
              <p className="sys-brand-on-surface text-body-medium">Target cohort: {preview.cohortName}</p>
            ) : null}
            {preview.plannedActions.length > 0 ? (
              <ul className="list-disc pl-6 sys-brand-on-surface text-body-medium">
                {preview.plannedActions.map((action) => (
                  <li key={action}>{action}</li>
                ))}
              </ul>
            ) : null}
          </div>
        ) : null}
      </div>
    </Dialog>
  );
}
